import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { PublicBlock } from "@/api/queries/publicChangelog";
import { useMyOrganizations } from "@/api/queries/orgs";
import { changelogAssetUrl } from "@/lib/changelogAsset";
import { cx } from "@/lib/utils";
import { tenantHopHref } from "@/shell/resolveShell";

/**
 * Renderer těla článku changelogu.
 *
 * Vzhled článků žije **tady a nikde jinde**. Autor plní sloty, tenhle
 * soubor rozhoduje, jak vypadají — takže redesign za rok znamená
 * přepsat jeden renderer a všechny články se překreslí. To je celý
 * důvod, proč je tělo strukturované a ne markdown.
 *
 * Neznámý typ bloku se **přeskočí**, ne aby shodil stránku: článek
 * publikovaný dnes musí přežít i to, že se schéma jednou rozšíří.
 */

function isVideo(key: string): boolean {
  return /\.(mp4|webm)$/i.test(key);
}

interface MediaRefShape {
  kind?: string;
  asset_key?: string;
  asset_keys?: string[];
  poster_asset_key?: string | null;
  fallback_asset_key?: string | null;
  alt?: string;
}

function mediaType(key: string): string | undefined {
  if (/\.webm$/i.test(key)) return "video/webm";
  if (/\.mp4$/i.test(key)) return "video/mp4";
  return undefined;
}

function MediaFallback({ alt }: { alt: string }): JSX.Element {
  return (
    <div
      role="img"
      aria-label={alt}
      className="flex min-h-40 w-full items-end rounded-lg border border-accent-border bg-gradient-to-br from-accent-bg via-surface-2 to-surface p-5"
      data-testid="changelog-media-fallback"
    >
      <span className="text-sm font-medium text-ink-2">{alt}</span>
    </div>
  );
}

function Media({ media }: { media: MediaRefShape }): JSX.Element | null {
  const keys = Array.from(
    new Set(
      [...(media.asset_keys ?? []), media.asset_key].filter(
        Boolean,
      ) as string[],
    ),
  );
  const signature = keys.join("|");
  const [primaryFailed, setPrimaryFailed] = useState(false);
  const [fallbackFailed, setFallbackFailed] = useState(false);
  const alt = media.alt ?? "";
  const fallbackKey =
    media.fallback_asset_key ?? media.poster_asset_key ?? null;

  useEffect(() => {
    setPrimaryFailed(false);
    setFallbackFailed(false);
  }, [signature, fallbackKey]);

  if (keys.length === 0) return <MediaFallback alt={alt} />;
  if (primaryFailed) {
    if (fallbackKey && !fallbackFailed && !keys.includes(fallbackKey)) {
      return (
        <img
          className="w-full rounded-lg border border-border"
          src={changelogAssetUrl(fallbackKey)}
          alt={alt}
          loading="lazy"
          onError={() => setFallbackFailed(true)}
          data-testid="changelog-media-fallback-image"
        />
      );
    }
    return <MediaFallback alt={alt} />;
  }

  // Krátká smyčka, ne GIF: desetinásobně menší a ostrá. ``muted`` je
  // podmínkou autoplay ve všech prohlížečích, ``playsInline`` brání
  // iOS otevřít video přes celou obrazovku.
  if (media.kind === "video" || keys.some(isVideo)) {
    return (
      <video
        className="w-full rounded-lg border border-border"
        autoPlay
        muted
        loop
        playsInline
        poster={
          media.poster_asset_key
            ? changelogAssetUrl(media.poster_asset_key)
            : undefined
        }
        aria-label={alt}
        onError={() => setPrimaryFailed(true)}
        data-testid="changelog-video"
      >
        {keys.map((key) => (
          <source
            key={key}
            src={changelogAssetUrl(key)}
            type={mediaType(key)}
          />
        ))}
      </video>
    );
  }
  const key = keys[0]!;
  return (
    <img
      className="w-full rounded-lg border border-border"
      src={changelogAssetUrl(key)}
      alt={alt}
      loading="lazy"
      onError={() => setPrimaryFailed(true)}
      data-testid="changelog-image"
    />
  );
}

export function localizedTenantCtaPath(
  path: string,
  lang: string,
): string | null {
  if (!path.startsWith("/") || path.startsWith("//") || path.includes("://")) {
    return null;
  }
  if (/^\/[a-z]{2,3}(?:-[a-z]{2,4})?(?:\/|$)/i.test(path)) return path;
  return `/${lang}${path}`;
}

function ChangelogCta({
  label,
  path,
}: {
  label: string;
  path: string;
}): JSX.Element | null {
  const { t, i18n } = useTranslation("common");
  const { organizations, isLoading } = useMyOrganizations();
  const [open, setOpen] = useState(false);
  const targetPath = localizedTenantCtaPath(path, i18n.language);
  if (!targetPath) return null;

  const adminOrganizations = organizations.filter(
    (org) => org.role === "admin",
  );
  const linkClass =
    "inline-flex rounded-md border border-accent bg-accent px-4 py-2 text-sm font-medium text-white";

  if (isLoading) {
    return (
      <button type="button" disabled className={`${linkClass} opacity-60`}>
        {label}
      </button>
    );
  }

  if (adminOrganizations.length === 0) {
    const redirectTo = `/${i18n.language}/changelog`;
    return (
      <a
        href={`/${i18n.language}/sign-in?redirect_to=${encodeURIComponent(redirectTo)}`}
        className={linkClass}
        data-testid="changelog-cta"
      >
        {t("changelog.ctaSignIn", "Přihlásit se a otevřít")}
      </a>
    );
  }

  if (adminOrganizations.length === 1) {
    const org = adminOrganizations[0]!;
    return (
      <a
        href={tenantHopHref(org.slug, targetPath)}
        target="_blank"
        rel="noreferrer"
        className={linkClass}
        data-testid="changelog-cta"
      >
        {label}
      </a>
    );
  }

  return (
    <span className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={linkClass}
        data-testid="changelog-cta"
      >
        {label}
      </button>
      {open && (
        <span
          role="menu"
          aria-label={t("changelog.ctaChooseOrg", "Vyberte organizaci")}
          className="absolute left-0 z-20 mt-2 min-w-56 rounded-md border border-border bg-surface p-1 shadow-lg"
          data-testid="changelog-cta-organizations"
        >
          {adminOrganizations.map((org) => (
            <a
              key={org.slug}
              role="menuitem"
              href={tenantHopHref(org.slug, targetPath)}
              target="_blank"
              rel="noreferrer"
              className="block rounded px-3 py-2 text-sm text-ink hover:bg-surface-2"
            >
              {org.name}
            </a>
          ))}
        </span>
      )}
    </span>
  );
}

/**
 * Značkový přebal pro článek bez vlastního vizuálu.
 *
 * Renderuje se v prohlížeči z metadat — žádná obrázková pipeline.
 * Existuje proto, že drtivá většina článků (a všechna zpětně doplněná
 * historie) obrázek mít nikdy nebude, a index s dírami vypadá hůř než
 * index bez fotek.
 */
export function ChangelogCover({
  title,
  versionTag,
  className = "",
}: {
  title: string;
  versionTag?: string | null;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={`flex items-end rounded-lg border border-accent-border bg-gradient-to-br from-accent-bg via-surface-2 to-surface p-5 ${className}`}
      data-testid="changelog-cover"
    >
      <span className="text-sm font-semibold text-ink-2">
        {versionTag ? `${versionTag} · ` : ""}
        {title}
      </span>
    </div>
  );
}

export function ChangelogBlocks({
  blocks,
  activeIndex,
  lang,
}: {
  blocks: PublicBlock[];
  /**
   * Blok, na kterém autor v redakci právě stojí — v náhledu se zvýrazní.
   *
   * Veřejná stránka prop nepředává a renderuje se přesně jako dosud:
   * bez obalu, bez rámečku, bez ``data-`` atributů navíc. Zvýraznění je
   * vlastnost **náhledu v editoru**, ne článku — osnova, formulář a náhled
   * jsou tři pohledy na tentýž blok a bez téhle vazby je autor páruje očima.
   */
  activeIndex?: number;
  /**
   * Jazyk článku. Pevné nadpisy šablony („Drobnosti") se sázejí jazykem
   * textu, ne jazykem rozhraní — redaktor s českým adminem, který píše
   * anglickou verzi, má v náhledu vidět anglický článek celý.
   */
  lang?: string;
}): JSX.Element {
  const { t: uiT, i18n } = useTranslation("common");
  const t = lang ? i18n.getFixedT(lang, "common") : uiT;

  const renderBlock = (
    block: PublicBlock,
    index: number,
  ): JSX.Element | null => {
    switch (block.type) {
      case "hero":
        return (
          <header key={index} className="space-y-3">
            <h1 className="text-3xl font-semibold tracking-tight text-ink">
              {String(block.title ?? "")}
            </h1>
            {block.subtitle != null && (
              <p className="text-lg text-ink-2">{String(block.subtitle)}</p>
            )}
            {block.media != null && (
              <Media media={block.media as MediaRefShape} />
            )}
          </header>
        );

      case "intro":
        return (
          <p key={index} className="text-lg leading-relaxed text-ink-2">
            {String(block.text ?? "")}
          </p>
        );

      case "feature":
        return (
          <section
            key={index}
            id={block.anchor ? String(block.anchor) : undefined}
            className="space-y-3"
            data-testid="changelog-feature"
          >
            <h2 className="text-xl font-semibold text-ink">
              {String(block.heading ?? "")}
            </h2>
            <p className="leading-relaxed text-ink-2">
              {String(block.body ?? "")}
            </p>
            {block.media != null && (
              <Media media={block.media as MediaRefShape} />
            )}
          </section>
        );

      case "minor_list": {
        const items = Array.isArray(block.items)
          ? (block.items as unknown[])
          : [];
        return (
          <section key={index} className="space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-3">
              {t("changelog.minorHeading", "Drobnosti")}
            </h2>
            <ul className="list-disc space-y-1 pl-5 text-ink-2">
              {items.map((item, i) => (
                <li key={i}>{String(item)}</li>
              ))}
            </ul>
          </section>
        );
      }

      case "media":
        return (
          <figure key={index} className="space-y-2">
            <Media media={block.media as MediaRefShape} />
            {block.caption != null && (
              <figcaption className="text-sm text-ink-3">
                {String(block.caption)}
              </figcaption>
            )}
          </figure>
        );

      case "cta": {
        const path = String(block.path ?? "");
        return (
          <p key={index}>
            <ChangelogCta label={String(block.label ?? "")} path={path} />
          </p>
        );
      }

      case "availability": {
        const plan = block.plan_name;
        const note = block.note;
        if (plan == null && note == null) return null;
        return (
          <p
            key={index}
            className="rounded-md border border-border bg-surface-2 px-4 py-3 text-sm text-ink-2"
            data-testid="changelog-availability"
          >
            {plan != null &&
              t("changelog.availableFrom", "Dostupné v tarifu {{plan}}.", {
                plan: String(plan),
              })}
            {plan != null && note != null ? " " : ""}
            {note != null && String(note)}
          </p>
        );
      }

      default:
        // Neznámý typ — nový blok, starý build. Přeskočit je
        // správně; spadnout na celé stránce kvůli jedné sekci ne.
        return null;
    }
  };

  return (
    <div className="space-y-8" data-testid="changelog-blocks">
      {blocks.map((block, index) => {
        const node = renderBlock(block, index);
        // Blok, který se nevykreslí (prázdná dostupnost, neznámý typ),
        // nedostane ani obal — prázdný zvýrazněný rámeček by tvrdil, že
        // na webu něco je.
        if (activeIndex === undefined || node === null) return node;
        return (
          <div
            key={index}
            className={cx(
              "-mx-2.5 rounded-md border px-2.5 py-2",
              index === activeIndex
                ? "border-accent bg-accent-bg"
                : "border-transparent",
            )}
            data-testid={`changelog-preview-block-${index}`}
            data-active={index === activeIndex ? "true" : undefined}
          >
            {node}
          </div>
        );
      })}
    </div>
  );
}
